import * as React from 'react';
import { AdminNotificationEntityAlertsDialog } from '@/components/admin/admin-notification-entity-alerts-dialog';
import { stripUrlSearchParams } from '@/lib/deep-link-resolve';
import {
  dedupeRedundantScenarioSopFlowAlerts,
  entityAlertFieldsFromApi,
  filterCriticalEntityAlerts,
  isCriticalNotificationSeverity,
  notificationMatchesEntityScope,
  resolveEntityAlertScope,
} from '@/lib/admin-notification-entity-scope';
import {
  ALERT_KEY_PARAM,
  isNotificationsInboxPath,
  readSurfaceAlertsFromUrl,
  SURFACE_ALERTS_PARAM,
} from '@/lib/admin-notification-fix-navigation';
import { fetchActiveAdminNotifications, type AdminNotification } from '@/lib/notifications';

const SURFACE_ALERT_STRIP_KEYS = [SURFACE_ALERTS_PARAM, ALERT_KEY_PARAM];

function sortEntityAlerts(rows: AdminNotification[]): AdminNotification[] {
  const severityRank = (severity: string) => {
    if (severity === 'error') return 0;
    if (severity === 'warning') return 1;
    return 2;
  };
  return [...rows].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity);
    if (bySeverity !== 0) return bySeverity;
    return b.timestamp - a.timestamp;
  });
}

export function useDestinationEntityAlertsModal() {
  const [open, setOpen] = React.useState(false);
  const [alerts, setAlerts] = React.useState<AdminNotification[]>([]);
  const [suppressedScenarioCount, setSuppressedScenarioCount] = React.useState(0);
  const handledRef = React.useRef(false);

  React.useEffect(() => {
    if (handledRef.current) return;

    const { surfaceAlerts, alertKey } = readSurfaceAlertsFromUrl();
    if (!surfaceAlerts || !alertKey) return;
    if (isNotificationsInboxPath()) return;

    handledRef.current = true;

    void (async () => {
      try {
        const active = await fetchActiveAdminNotifications();
        const trigger = active.find((row) => row.key === alertKey);
        if (!trigger) {
          stripUrlSearchParams(SURFACE_ALERT_STRIP_KEYS);
          return;
        }

        const scope = resolveEntityAlertScope(entityAlertFieldsFromApi(trigger));
        const matched = sortEntityAlerts(
          active.filter((row) => {
            const fields = entityAlertFieldsFromApi(row);
            return isCriticalNotificationSeverity(fields.severity)
              && notificationMatchesEntityScope(fields, scope);
          }),
        );

        const { alerts: deduped, suppressedScenarioCount: suppressed } =
          dedupeRedundantScenarioSopFlowAlerts(matched);

        if (deduped.length === 0) {
          stripUrlSearchParams(SURFACE_ALERT_STRIP_KEYS);
          return;
        }

        setAlerts(deduped);
        setSuppressedScenarioCount(suppressed);
        setOpen(true);
        stripUrlSearchParams(SURFACE_ALERT_STRIP_KEYS);
      } catch {
        stripUrlSearchParams(SURFACE_ALERT_STRIP_KEYS);
      }
    })();
  }, []);

  const dialog = (
    <AdminNotificationEntityAlertsDialog
      alerts={alerts}
      open={open}
      suppressedScenarioCount={suppressedScenarioCount}
      onOpenChange={setOpen}
    />
  );

  return { dialog };
}
